import React, { useEffect } from 'react';

const OpenClosedPrinciple = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Open/Closed Principle (OCP) - SOLID Principles Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Open/Closed Principle - software entities should be open for extension but closed for modification. Learn with real-world examples and design patterns.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Open/Closed Principle - software entities should be open for extension but closed for modification. Learn with real-world examples and design patterns.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'open closed principle, OCP, SOLID principles, extensibility, polymorphism, strategy pattern, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'open closed principle, OCP, SOLID principles, extensibility, polymorphism, strategy pattern, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Open/Closed Principle (OCP)</h1>
        <p style={styles.subtitle}>
          The Second SOLID Principle - Open for extension, closed for modification
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Definition</h2>
          <blockquote style={styles.blockquote}>
            "Software entities (classes, modules, functions, etc.) should be open for extension but closed for modification."
          </blockquote>
          <p style={styles.paragraph}>
            The Open/Closed Principle means that you should be able to extend a class's behavior without modifying it. 
            This is achieved through abstraction and polymorphism, allowing new functionality to be added through 
            inheritance or composition rather than changing existing code.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Key Concepts</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Open for Extension:</strong> New behavior can be added to meet new requirements</li>
            <li style={styles.listItem}><strong>Closed for Modification:</strong> Existing source code should not be changed</li>
            <li style={styles.listItem}><strong>Abstraction:</strong> Use abstract classes and interfaces to define contracts</li>
            <li style={styles.listItem}><strong>Polymorphism:</strong> Different implementations can be used interchangeably</li>
            <li style={styles.listItem}><strong>Plugin Architecture:</strong> New features can be "plugged in" without changing core code</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Violation Example</h2>
          <div style={styles.codeBlock}>
{`// BAD: Violates OCP - Need to modify existing code for new shapes
class AreaCalculator {
    public double calculateArea(Object shape) {
        if (shape instanceof Rectangle) {
            Rectangle rectangle = (Rectangle) shape;
            return rectangle.getWidth() * rectangle.getHeight();
        } else if (shape instanceof Circle) {
            Circle circle = (Circle) shape;
            return Math.PI * circle.getRadius() * circle.getRadius();
        }
        // Problem: Need to add more if-else for new shapes
        else if (shape instanceof Triangle) {
            Triangle triangle = (Triangle) shape;
            return 0.5 * triangle.getBase() * triangle.getHeight();
        }
        return 0;
    }
}`}
          </div>
          <p style={styles.paragraph}><strong>Problems with this approach:</strong></p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Every new shape requires modifying the AreaCalculator class</li>
            <li style={styles.listItem}>Risk of breaking existing functionality</li>
            <li style={styles.listItem}>Violates Single Responsibility Principle as well</li>
            <li style={styles.listItem}>Difficult to test new shapes independently</li>
            <li style={styles.listItem}>Code becomes increasingly complex with more shapes</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Correct Implementation</h2>
          <div style={styles.codeBlock}>
{`// GOOD: Following OCP - Open for extension, closed for modification

// Abstract base class defining the contract
abstract class Shape {
    public abstract double calculateArea();
}

// Existing implementations - never need to change
class Rectangle extends Shape {
    private double width;
    private double height;
    
    public Rectangle(double width, double height) {
        this.width = width;
        this.height = height;
    }
    
    @Override
    public double calculateArea() {
        return width * height;
    }
}

class Circle extends Shape {
    private double radius;
    
    public Circle(double radius) {
        this.radius = radius;
    }
    
    @Override
    public double calculateArea() {
        return Math.PI * radius * radius;
    }
}

// New shapes can be added without modifying existing code
class Triangle extends Shape {
    private double base;
    private double height;
    
    public Triangle(double base, double height) {
        this.base = base;
        this.height = height;
    }
    
    @Override
    public double calculateArea() {
        return 0.5 * base * height;
    }
}

// Calculator class never needs to change
class AreaCalculator {
    public double calculateArea(Shape shape) {
        return shape.calculateArea();
    }
    
    public double calculateTotalArea(List<Shape> shapes) {
        return shapes.stream()
                    .mapToDouble(Shape::calculateArea)
                    .sum();
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Benefits of Following OCP</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Stability:</strong> Existing code remains unchanged and stable</li>
            <li style={styles.listItem}><strong>Testability:</strong> New features can be tested in isolation</li>
            <li style={styles.listItem}><strong>Maintainability:</strong> Easier to add features without breaking existing ones</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> System can be extended in ways not originally anticipated</li>
            <li style={styles.listItem}><strong>Reduced Risk:</strong> No chance of introducing bugs in working code</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Open/Closed Principle is crucial for building maintainable and extensible software systems. 
            By designing classes that are open for extension but closed for modification, we create stable 
            foundations that can grow and adapt to new requirements without risking existing functionality.
          </p>
          <p style={styles.paragraph}>
            The key is to identify areas where change is likely and create appropriate abstractions. 
            Use patterns like Strategy, Template Method, and Observer to achieve OCP compliance. 
            Remember that achieving OCP often requires careful planning and good understanding of the domain.
          </p>
        </section>
      </main>
    </article>
  );
};

export default OpenClosedPrinciple;
